import { AlertCircle, Check, Loader2, Zap } from 'lucide-react';

import { BulkRegisterRow, type BulkRegisterRowData } from './BulkRegisterRow';

const CHIP_SUCCESS =
  'inline-flex items-center gap-1 rounded-full border border-success-deep bg-success-bg px-2 py-0.5 font-bold text-success';

const CHIP_FAILED =
  'inline-flex items-center gap-1 rounded-full border border-danger-border bg-danger-bg px-2 py-0.5 font-bold text-danger';

interface BulkRegisterTableProps {
  rows: BulkRegisterRowData[];
  /** Hình dạng nút ở chân bảng — xem `action` bên `AccountBulkRegisterPanel`. */
  action: { done: boolean; disabled: boolean; label: string; className: string };
  submitting: boolean;
  onStart: () => void;
}

/** Bảng kết quả đăng ký hàng loạt: đầu bảng đếm, thân dòng, chân nút chạy/chạy lại. */
export function BulkRegisterTable({ rows, action, submitting, onStart }: BulkRegisterTableProps) {
  const successCount = rows.filter((row) => row.status === 'success').length;
  const failedCount = rows.filter((row) => row.status === 'failed').length;

  return (
    <div className="border-line bg-panel overflow-hidden rounded-xl border">
      <div className="border-line flex items-center justify-between border-b px-5 py-4">
        <div className="text-ink flex items-center gap-2 text-[14px] font-semibold">
          <Zap size={16} />
          <span>Danh sách tài khoản</span>
          <span className="bg-accent-soft text-accent rounded-full px-2 py-0.5 text-[11px] font-bold">
            {rows.length}
          </span>
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          {successCount > 0 && (
            <span className={CHIP_SUCCESS}>
              <Check size={10} />
              {successCount} thành công
            </span>
          )}
          {failedCount > 0 && (
            <span className={CHIP_FAILED}>
              <AlertCircle size={10} />
              {failedCount} thất bại
            </span>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="bg-canvas-raised">
              <th className="text-ink-faint w-[60px] px-5 py-2.5 text-center text-[10px] font-bold uppercase tracking-[0.1em]">
                STT
              </th>
              <th className="text-ink-faint px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]">
                Proxy
              </th>
              <th className="text-ink-faint px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]">
                Kết quả
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <BulkRegisterRow key={row.proxy} row={row} />
            ))}
          </tbody>
        </table>
      </div>

      <div className="border-line bg-canvas-raised flex items-center justify-between border-t px-5 py-4">
        <p className="text-ink-dim text-[11px]">
          Mật khẩu chỉ hiện đúng lần này — chép lại trước khi rời trang.
        </p>

        <button
          type="button"
          onClick={() => {
            if (!action.done) onStart();
          }}
          disabled={action.disabled}
          className={`flex items-center gap-1.5 rounded-lg px-5 py-2.5 text-[13px] font-bold transition-colors disabled:cursor-not-allowed ${action.className}`}
        >
          {action.done ? (
            <Check size={14} />
          ) : (
            submitting && <Loader2 size={14} className="animate-spin" />
          )}
          {action.label}
        </button>
      </div>
    </div>
  );
}
